const segmenter =
  typeof Intl.Segmenter === "function"
    ? new Intl.Segmenter(undefined, { granularity: "grapheme" })
    : null;

// Mirrors Catalog::CharacterSet#unsupported_characters. Split into what the
// customer sees as one character, so é typed as e plus an accent, or an emoji
// with a skin tone, is reported whole rather than in pieces
export default function unsupportedCharacters(text, allowedCharacters) {
  const normalised = (text ?? "").normalize("NFC");
  const characters = segmenter
    ? Array.from(segmenter.segment(normalised), ({ segment }) => segment)
    : Array.from(normalised);
  const allowed = new Set(Array.from(allowedCharacters));

  return [...new Set(characters)].filter(char => !allowed.has(char));
}

// Predefined strings still carry the flags their sheets were saved with
export function characterSetKeyFor(predefinedString) {
  if (predefinedString.useRegexAZ) return "foil_monogram";
  if (predefinedString.forDaisyWheelPrinter) return "daisy_wheel";

  return null;
}

// The set's characters other than letters, numbers and space, for messages
// that list what can be used
export function symbolsIn({ allowedCharacters }) {
  return Array.from(allowedCharacters)
    .filter(char => !/[A-Za-z0-9 ]/.test(char))
    .join(" ");
}

function escapeForClass(allowedCharacters) {
  return Array.from(allowedCharacters, char =>
    char.replace(/[\\\]^-]/g, "\\$&")
  ).join("");
}

// For inputs that test the whole value against a RegExp
export function characterSetPattern({ allowedCharacters, allowLineBreaks }) {
  const escaped = escapeForClass(allowedCharacters);

  return new RegExp(`^[${escaped}${allowLineBreaks ? "\\n" : ""}]+$`);
}

// Matches the first character outside the set, for checks that strip or flag
// text one character at a time. \r counts as part of a line break, since
// pasted Windows text arrives as \r\n
export function unprintablePattern({ allowedCharacters, allowLineBreaks }) {
  const escaped = escapeForClass(allowedCharacters);

  return new RegExp(`[^${escaped}${allowLineBreaks ? "\\r\\n" : ""}]`, "u");
}

// End of the character the customer sees at index: an emoji with a skin tone
// or a flag is several code units, and stripping only the first leaves the
// rest behind as broken symbols
export function characterEndAt(text, index) {
  if (!segmenter) return index + Array.from(text.slice(index))[0].length;

  const { segment, index: start } = segmenter.segment(text).containing(index);

  return start + segment.length;
}

// What a font can print: the editor set, plus the sets fonts.yml lists for it,
// minus the characters its file lacks
export function fontCharacterSet(characterSets, font) {
  const { editor } = characterSets;
  const extra = (font.character_sets ?? [])
    .map(key => characterSets[key].allowedCharacters)
    .join("");
  const missing = new Set(Array.from(font.missing_characters ?? ""));

  return {
    allowedCharacters: Array.from(editor.allowedCharacters + extra)
      .filter(char => !missing.has(char))
      .join(""),
    allowLineBreaks: editor.allowLineBreaks
  };
}

// Keyed by each weight's name, which is the fontFamily text styles store
export function unprintablePatternsByFontFamily(characterSets, fonts) {
  const patterns = new Map();

  fonts.forEach(font => {
    const pattern = unprintablePattern(fontCharacterSet(characterSets, font));
    Object.values(font.weights).forEach(weight =>
      patterns.set(weight.name, pattern)
    );
  });

  return patterns;
}

// Every part of an emoji: the pictograph, flag letters, skin tones, and the
// joiners, selectors and tag characters (England, Scotland, Wales flags) that
// combine them
export const emojiPattern =
  /\p{Extended_Pictographic}|\p{Regional_Indicator}|\p{Emoji_Modifier}|\u200D|\uFE0F|\u20E3|[\u{E0020}-\u{E007F}]/gu;

// Each emoji in the text once, whole, as the customer sees it
export function emojiIn(text) {
  const characters = segmenter
    ? Array.from(segmenter.segment(text), ({ segment }) => segment)
    : Array.from(text);

  return [
    ...new Set(characters.filter(char => char.search(emojiPattern) !== -1))
  ];
}

// Most spaces or line breaks allowed in a row in Daisy Wheel printer text. Longer
// runs were used to split one foil line into two at either edge of the cover,
// which the printer can't place where the editor shows it
export const daisyWheelMaxWhitespaceRun = 3;
